import React, { useCallback, useEffect, useRef, useState } from "react";
import SosAlertPopup from "./SosAlertPopup";
import useSosWatch from "./useSosWatch";
import { createSosAlarm } from "./sosAlarm";
import { sosTabTitle } from "./sosAlertRules";
import "./sosAlert.css";

const canNotify = () => typeof window !== "undefined" && "Notification" in window;

/** Desktop notification for each new SOS while the tab is hidden / minimized (title "⚠ SOS Alert", the SOS message). */
function notifyDesktop(items) {
  if (!canNotify() || window.Notification.permission !== "granted" || document.visibilityState === "visible") return;
  items.forEach((a) => {
    const note = new window.Notification("⚠ SOS Alert", { body: a.message || a.title || "SOS", tag: `sos-${a.id}`, requireInteraction: true });
    note.onclick = () => {
      window.focus();
      note.close();
    };
  });
}

/**
 * Live SOS alert of the logged-in website (in the authorised layout): checks for new crew SOS, shows the red popup, plays
 * the SOS alarm until Close / Mute, shows a desktop notification when the tab is hidden and blinks the tab title until
 * the tab is looked at. The browser asks once (first click) to allow desktop notifications.
 */
export default function SosAlertCenter() {
  const alarm = useRef(null);
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  mutedRef.current = muted;

  const onNew = useCallback((items) => {
    if (!alarm.current) alarm.current = createSosAlarm();
    if (!mutedRef.current) alarm.current.start();
    notifyDesktop(items);
  }, []);
  const { alerts, dismiss } = useSosWatch({ onNew });

  // Sound is on by default: browsers allow it only after a click / key press on the page, so every click or key press
  // unlocks it (cheap when already allowed); an SOS that arrived before the first click starts sounding at that click.
  useEffect(() => {
    if (!alarm.current) alarm.current = createSosAlarm();
    const unlock = () => {
      if (alarm.current) alarm.current.unlock();
    };
    document.addEventListener("pointerdown", unlock, true);
    document.addEventListener("keydown", unlock, true);
    return () => {
      document.removeEventListener("pointerdown", unlock, true);
      document.removeEventListener("keydown", unlock, true);
    };
  }, []);

  // Ask once for desktop notifications, on the first click (browsers only ask after a user action).
  useEffect(() => {
    if (!canNotify() || window.Notification.permission !== "default") return undefined;
    const ask = () => {
      window.Notification.requestPermission().catch(() => {});
    };
    document.addEventListener("click", ask, { once: true });
    return () => document.removeEventListener("click", ask);
  }, []);

  // Blink the tab title while there are SOS and the tab is not being looked at.
  useEffect(() => {
    if (!alerts.length) return undefined;
    const original = document.title;
    let on = false;
    const timer = setInterval(() => {
      on = document.visibilityState !== "visible" && !on;
      document.title = on ? sosTabTitle(alerts.length) : original;
    }, 1000);
    return () => {
      clearInterval(timer);
      document.title = original;
    };
  }, [alerts.length]);

  useEffect(
    () => () => {
      if (alarm.current) alarm.current.close();
    },
    [],
  );

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (!alarm.current) return;
    if (next) alarm.current.stop();
    else alarm.current.start();
  };

  const close = () => {
    if (alarm.current) alarm.current.stop();
    setMuted(false);
    dismiss();
  };

  return <SosAlertPopup alerts={alerts} muted={muted} onMute={toggleMute} onClose={close} />;
}
